/** Wiki Health page (EN source of truth + VI); spread into the main dictionaries */

export const healthEn = {
  'nav.health': 'Health',
  'health.title': 'Wiki Health',
  'health.subtitle': 'Checks this project\'s wiki for pages citing replaced Sources, possible contradictions between pages, pages with no new Sources for a while, and searches that found nothing. Computed from the wiki itself, no AI and no credits.',
  'health.loadError': 'Could not load the health report.',
  'health.empty': 'Nothing to report.',
  'health.filter.all': 'All ({count})',
  'health.filter.superseded_source': 'Replaced sources ({count})',
  'health.filter.contradiction': 'Possible contradictions ({count})',
  'health.filter.stale_page': 'Stale pages ({count})',
  'health.filter.search_gap': 'No results ({count})',
  'health.staleDays': 'Stale after',
  'health.staleDays.option': '{days} days',
  'health.showDismissed': 'Show dismissed ({count})',
  'health.truncated': 'Large project: some checks stopped at their limit, so not every finding is listed.',
  'health.pages': 'Findings pages',
  'health.previous': 'Previous',
  'health.next': 'Next',
  'health.range': '{from}-{to} of {total}',
  'health.fixCertain': 'Apply certain fixes',
  'health.fixCertain.hint': 'A fix is certain only when the newer version of a Source has the same text (re-pushed with only whitespace or line-ending changes); any other change is proposed for review. Fixes go through review: added at once in a one-member workspace, waiting for a reviewer in a team.',
  'health.fixCertain.done': '{applied} added, {pending} waiting for review, {failed} failed',
  'health.fixCertain.none': 'No certain fixes to apply.',
  'health.needsEditor': 'Only editors and admins can dismiss findings or propose fixes.',
  'health.action.open': 'Open',
  'health.action.dismiss': 'Dismiss',
  'health.action.restore': 'Restore',
  'health.action.propose': 'Propose fix',
  'health.action.failed': 'Action failed: {message}',
  'health.proposed.applied': 'Citation updated',
  'health.proposed.pending': 'Fix proposed; waiting for review',
  'health.proposed.view': 'View',
  'health.dismissed': 'Dismissed',
  'health.superseded.title': '{page} cites a replaced Source',
  'health.superseded.detail': '"{source}" in {count} sentence(s); newer version: {replacement}',
  'health.superseded.noReplacement': 'no newer version',
  'health.superseded.certain': 'Certain fix',
  'health.superseded.uncertain': 'Needs review: the newer version changed the text',
  'health.contradiction.title': 'Possible contradiction ({reason})',
  'health.contradiction.number': 'different numbers',
  'health.contradiction.negation': 'one sentence is negated',
  'health.contradiction.hint': 'A lexical check: read both pages before changing either.',
  'health.stale.title': '{page} has no new Sources',
  'health.stale.sources_changed': 'Newest cited Source is {days} days old and a cited Source has a newer version. Recompile or update the page.',
  'health.stale.no_new_sources': 'Newest cited Source is {days} days old. Push newer Sources if the topic moved on.',
  'health.gap.title': 'No results for "{query}"',
  'health.gap.detail': '{count} time(s) in the last 30 days ({kinds}). Push a Source that answers it, then compile.',
}

export const healthVi: Record<keyof typeof healthEn, string> = {
  'nav.health': 'Sức khỏe',
  'health.title': 'Sức khỏe wiki',
  'health.subtitle': 'Kiểm tra wiki của dự án: trang trích dẫn Nguồn đã bị thay thế, chỗ có thể mâu thuẫn giữa các trang, trang lâu không có Nguồn mới, và lượt tìm không có kết quả. Tính từ chính wiki, không dùng AI, không tốn credit.',
  'health.loadError': 'Không tải được báo cáo sức khỏe.',
  'health.empty': 'Không có gì cần báo.',
  'health.filter.all': 'Tất cả ({count})',
  'health.filter.superseded_source': 'Nguồn bị thay thế ({count})',
  'health.filter.contradiction': 'Có thể mâu thuẫn ({count})',
  'health.filter.stale_page': 'Trang cũ ({count})',
  'health.filter.search_gap': 'Không có kết quả ({count})',
  'health.staleDays': 'Coi là cũ sau',
  'health.staleDays.option': '{days} ngày',
  'health.showDismissed': 'Hiện mục đã bỏ qua ({count})',
  'health.truncated': 'Dự án lớn: một số bước kiểm tra dừng ở giới hạn, nên chưa liệt kê hết.',
  'health.pages': 'Trang kết quả',
  'health.previous': 'Trước',
  'health.next': 'Sau',
  'health.range': '{from}-{to} / {total}',
  'health.fixCertain': 'Áp dụng các sửa chắc chắn',
  'health.fixCertain.hint': 'Một sửa đổi chỉ chắc chắn khi bản mới của Nguồn có nội dung giống hệt (đẩy lại, chỉ khác khoảng trắng hoặc ký tự xuống dòng); mọi thay đổi khác đều được đề xuất để duyệt. Sửa đổi đi qua bước duyệt: vào ngay ở workspace một người, chờ người duyệt ở workspace nhóm.',
  'health.fixCertain.done': '{applied} đã thêm, {pending} chờ duyệt, {failed} thất bại',
  'health.fixCertain.none': 'Không có sửa đổi chắc chắn nào.',
  'health.needsEditor': 'Chỉ vai trò Biên tập hoặc Quản trị mới bỏ qua mục hoặc đề xuất sửa được.',
  'health.action.open': 'Mở',
  'health.action.dismiss': 'Bỏ qua',
  'health.action.restore': 'Hiện lại',
  'health.action.propose': 'Đề xuất sửa',
  'health.action.failed': 'Không thực hiện được: {message}',
  'health.proposed.applied': 'Đã cập nhật trích dẫn',
  'health.proposed.pending': 'Đã đề xuất sửa, đang chờ duyệt',
  'health.proposed.view': 'Xem',
  'health.dismissed': 'Đã bỏ qua',
  'health.superseded.title': '{page} trích dẫn Nguồn đã bị thay thế',
  'health.superseded.detail': '"{source}" trong {count} câu; bản mới: {replacement}',
  'health.superseded.noReplacement': 'không có bản mới',
  'health.superseded.certain': 'Sửa chắc chắn',
  'health.superseded.uncertain': 'Cần duyệt: bản mới đã thay đổi nội dung',
  'health.contradiction.title': 'Có thể mâu thuẫn ({reason})',
  'health.contradiction.number': 'khác số liệu',
  'health.contradiction.negation': 'một câu mang nghĩa phủ định',
  'health.contradiction.hint': 'Kiểm tra theo từ vựng: hãy đọc cả hai trang trước khi sửa.',
  'health.stale.title': '{page} không có Nguồn mới',
  'health.stale.sources_changed': 'Nguồn mới nhất được trích dẫn đã {days} ngày tuổi và có Nguồn được trích dẫn đã có bản mới. Hãy biên soạn lại hoặc cập nhật trang.',
  'health.stale.no_new_sources': 'Nguồn mới nhất được trích dẫn đã {days} ngày tuổi. Đẩy Nguồn mới hơn nếu chủ đề đã thay đổi.',
  'health.gap.title': 'Không có kết quả cho "{query}"',
  'health.gap.detail': '{count} lần trong 30 ngày qua ({kinds}). Hãy đẩy một Nguồn trả lời được, rồi biên soạn.',
}
